import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home, MessageCircleHeart, BookOpen, TrendingUp, User, Milestone, HandHeart,
  Phone, Wind, GraduationCap, MessagesSquare, BookMarked, Users, Sparkles, LayoutGrid, X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';

export default function BottomNav() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const role = user?.is_admin ? 'admin' : (user?.role || 'member');
  const isSupporter = role === 'supporter';

  // Full destination list per role (mirrors the desktop TopNav so every section
  // is reachable on phones via the "More" sheet).
  const memberAll = [
    { to: '/app', icon: Home, label: t('nav.home'), testid: 'nav-home' },
    { to: '/app/learn', icon: BookOpen, label: t('nav.learn'), testid: 'nav-learn' },
    { to: '/app/coach', icon: MessageCircleHeart, label: t('nav.coach'), testid: 'nav-coach' },
    { to: '/app/community', icon: MessagesSquare, label: 'Community', testid: 'nav-community' },
    { to: '/app/plans', icon: Milestone, label: t('nav.plans', 'Plans'), testid: 'nav-plans' },
    { to: '/app/journal', icon: Sparkles, label: t('nav.journal'), testid: 'nav-journal' },
    { to: '/app/lounge', icon: Wind, label: 'Lounge', testid: 'nav-lounge' },
    { to: '/app/support', icon: HandHeart, label: t('nav.support'), testid: 'nav-support' },
    { to: '/app/therapists', icon: Users, label: t('nav.therapy'), testid: 'nav-therapists' },
    { to: '/app/resources', icon: BookMarked, label: 'Resources', testid: 'nav-resources' },
    { to: '/app/progress', icon: TrendingUp, label: t('nav.progress'), testid: 'nav-progress' },
    { to: '/app/profile', icon: User, label: t('nav.profile'), testid: 'nav-profile' },
  ];
  const supporterAll = [
    { to: '/app/support-home', icon: HandHeart, label: 'Home', testid: 'nav-support-home' },
    { to: '/app/support-coach', icon: MessageCircleHeart, label: 'Coach', testid: 'nav-support-coach' },
    { to: '/app/support-community', icon: MessagesSquare, label: 'Community', testid: 'nav-supporter-community' },
    { to: '/app/support-university', icon: GraduationCap, label: 'University', testid: 'nav-support-university' },
    { to: '/app/therapists', icon: Users, label: 'Therapists', testid: 'nav-supporter-therapists' },
    { to: '/app/resources', icon: BookMarked, label: 'Resources', testid: 'nav-supporter-resources' },
    { to: '/app/support-crisis', icon: Phone, label: 'Crisis', testid: 'nav-support-crisis' },
    { to: '/app/profile', icon: User, label: t('nav.profile'), testid: 'nav-profile' },
  ];

  const allItems = isSupporter ? supporterAll : memberAll;
  // 4 primary tabs in the bar; everything else lives in the "More" sheet.
  const primaryCount = 4;
  const primary = allItems.slice(0, primaryCount);
  const homeEnd = isSupporter ? '/app/support-home' : '/app';

  const tabClass = ({ isActive }) =>
    `flex flex-col items-center gap-1 pt-1.5 pb-1 rounded-2xl transition-smooth min-h-[48px] ${
      isActive ? 'text-navy' : 'text-navy/45 hover:text-navy'
    }`;

  return (
    <>
      {/* "More" sheet — lists every section so nothing is unreachable on phones */}
      {moreOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end"
          onClick={() => setMoreOpen(false)}
          data-testid="bottom-nav-more-sheet"
        >
          <div
            className="w-full bg-white rounded-t-3xl p-5 pb-8 animate-fade-up"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="font-heading text-xl">All sections</p>
              <button
                onClick={() => setMoreOpen(false)}
                data-testid="bottom-nav-more-close"
                aria-label="Close menu"
                className="w-9 h-9 rounded-full flex items-center justify-center text-navy/50 hover:text-navy hover:bg-beige transition-smooth"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {allItems.map(({ to, icon: Icon, label, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === homeEnd}
                  data-testid={`more-${testid}`}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-2 py-4 px-1 rounded-2xl transition-smooth ${
                      isActive ? 'bg-navy text-white' : 'bg-beige/60 text-navy/75 hover:bg-beige'
                    }`
                  }
                >
                  <Icon size={22} strokeWidth={1.75} />
                  <span className="text-[11px] font-medium text-center leading-tight">{label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}

      <nav
        data-testid="bottom-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-navy/5"
      >
        <div className="max-w-md mx-auto grid grid-cols-5 gap-0.5 px-1 py-2">
          {primary.map(({ to, icon: Icon, label, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === homeEnd}
              data-testid={testid}
              className={tabClass}
            >
              {({ isActive }) => (
                <>
                  <span className={`flex items-center justify-center w-11 h-7 rounded-full transition-smooth ${isActive ? 'bg-navy/10' : ''}`}>
                    <Icon size={21} strokeWidth={isActive ? 2.2 : 1.6} />
                  </span>
                  <span className="text-[11px] font-medium tracking-wide">{label}</span>
                </>
              )}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(true)}
            data-testid="nav-more"
            aria-label="More sections"
            className="flex flex-col items-center gap-1 pt-1.5 pb-1 rounded-2xl transition-smooth min-h-[48px] text-navy/45 hover:text-navy"
          >
            <span className="flex items-center justify-center w-11 h-7 rounded-full">
              <LayoutGrid size={21} strokeWidth={1.6} />
            </span>
            <span className="text-[11px] font-medium tracking-wide">More</span>
          </button>
        </div>
        <div className="h-safe" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
      </nav>
    </>
  );
}
